import { Controller } from "@hotwired/stimulus";

/**
 * Drawer Controller
 *
 * This controller manages a drawer panel that slides in from the left side of the screen.
 * It handles opening, closing, and animating the drawer, as well as trapping focus and
 * managing the backdrop.
 *
 * Targets:
 * - content: The main content of the drawer
 * - backdrop: The semi-transparent background that appears behind the drawer
 * - closeButton: Button to close the drawer
 *
 * Classes:
 * - openClass: Applied to the drawer when it's open (default: "translate-x-0")
 * - closedClass: Applied to the drawer when it's closed (default: "-translate-x-full")
 * - backdropOpenClass: Applied to the backdrop when open (default: "opacity-50 pointer-events-auto")
 * - backdropClosedClass: Applied to the backdrop when closed (default: "opacity-0 pointer-events-none")
 */
export default class extends Controller {
  static targets = ["container", "content", "backdrop", "closeButton"];

  static values = {
    open: { type: Boolean, default: false },
    trapFocus: { type: Boolean, default: true },
    openClass: { type: String, default: "translate-x-0" },
    closedClass: { type: String, default: "-translate-x-full" },
    backdropOpenClass: {
      type: String,
      default: "opacity-50 pointer-events-auto",
    },
    backdropClosedClass: {
      type: String,
      default: "opacity-0 pointer-events-none",
    },
  };

  initialize() {
    // Bind methods to this
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connect() {
    this._isOpen = false;

    // Set initial state based on openValue
    if (this.openValue) {
      this.open();
    } else {
      this.close();
    }

    // Listen for drawer:open event
    this._openHandler = () => this.open();
    this._closeHandler = () => this.close();
    window.addEventListener('drawer:open', this._openHandler);

    // Listen for drawer:close event
    window.addEventListener('drawer:close', this._closeHandler);

    // Auto-open drawer when drawer_content is updated via Turbo Stream
    this._streamHandler = (event) => {
      const target = event.target;
      if (target.getAttribute && target.getAttribute('target') === 'drawer_content') {
        requestAnimationFrame(() => this.open());
      }
    };
    document.addEventListener('turbo:before-stream-render', this._streamHandler);

    // Close the drawer when clicking outside
    if (this.hasBackdropTarget) {
      this.backdropTarget.addEventListener("click", () => this.close());
    }

    // Close on ESC key
    document.addEventListener("keydown", this.handleKeyDown);
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener('drawer:open', this._openHandler);
    window.removeEventListener('drawer:close', this._closeHandler);
    document.removeEventListener('turbo:before-stream-render', this._streamHandler);
  }

  handleKeyDown(event) {
    if (event.key === "Escape" && this.openValue) {
      this.close();
    }
  }

  openValueChanged(isOpen) {
    if (isOpen) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    if (this._isOpen) return;
    this._isOpen = true;

    if (this.hasContainerTarget) {
      this.containerTarget.classList.remove("hidden");
    }

    if (this.hasContentTarget) {
      this.contentTarget.classList.remove(...this.closedClassValue.split(" "));
      this.contentTarget.classList.add(...this.openClassValue.split(" "));
    }

    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove(
        ...this.backdropClosedClassValue.split(" ")
      );
      this.backdropTarget.classList.add(
        ...this.backdropOpenClassValue.split(" ")
      );
    }

    // Set value after animation to ensure proper state
    setTimeout(() => {
      this.openValue = true;

      // Focus the close button if available
      if (this.hasCloseButtonTarget && this.trapFocusValue) {
        this.closeButtonTarget.focus();
      }

      // Dispatch custom event
      this.dispatch("opened");
    }, 300);

    // Prevent body scrolling when drawer is open
    document.body.classList.add("overflow-hidden");
  }

  close() {
    if (!this._isOpen) return;
    this._isOpen = false;

    if (this.hasContainerTarget) {
      this.containerTarget.classList.add("hidden");
    }

    if (this.hasContentTarget) {
      this.contentTarget.classList.remove(...this.openClassValue.split(" "));
      this.contentTarget.classList.add(...this.closedClassValue.split(" "));
    }

    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove(
        ...this.backdropOpenClassValue.split(" ")
      );
      this.backdropTarget.classList.add(
        ...this.backdropClosedClassValue.split(" ")
      );
    }

    // Set value after animation to ensure proper state
    setTimeout(() => {
      this.openValue = false;

      // Dispatch custom event
      this.dispatch("closed");
    }, 300);

    // Restore body scrolling
    document.body.classList.remove("overflow-hidden");
  }

  toggle() {
    if (this.openValue) {
      this.close();
    } else {
      this.open();
    }
  }
}
